import { type FC, useMemo, useState } from "react";
import { styled } from "@linaria/react";
import { Row } from "@/components/common/row.ts";
import { ButtonGeneric } from "@/components/buttons/ButtonGeneric.tsx";
import { IconX } from "@/icons/IconX.tsx";
import { theme } from "@/themes/theme.ts";
import { postBulkUpdateUserEvents, postFlowTutorMessage } from "@/api/admin.ts";
import { toast } from "sonner";
import { toastAndError, toastAndWarn } from "@/helpers/toast.ts";
import {
  ModalBackground,
  ModalClose,
  ModalContent,
  ModalHeader,
} from "@/components/modal/shared.ts";

const Description = styled.p`
  color: #475569;
  font-size: var(--14px);
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: var(--14px);
  font-weight: 600;
  color: ${theme.brandTextPrimary};
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: ${theme.brandBorderRadius};
  font-size: var(--14px);
  color: ${theme.brandTextPrimary};
  background: white;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const awardActions = [
  { value: "Awarded50", key: "50 Coins" },
  { value: "Awarded100", key: "100 Coins" },
  { value: "Awarded150", key: "150 Coins" },
  { value: "Awarded200", key: "200 Coins" },
  { value: "Awarded250", key: "250 Coins" },
  { value: "Awarded300", key: "300 Coins" },
  { value: "Awarded350", key: "350 Coins" },
  { value: "Awarded400", key: "400 Coins" },
  { value: "Awarded450", key: "450 Coins" },
  { value: "Awarded500", key: "500 Coins" },
] as const;

interface AwardStudentCoinsModalProps {
  flowGeneratorSessionId: number;
  isOpen: boolean;
  onClose: () => void;
  studentUserId?: number;
}

export const ModalAwardStudentCoins: FC<AwardStudentCoinsModalProps> = ({
  flowGeneratorSessionId,
  isOpen,
  onClose,
  studentUserId,
}) => {
  const [awardAction, setAwardAction] = useState("");

  if (!isOpen && awardAction != "") {
    setAwardAction("");
  }

  const selectedAward = useMemo(
    () => awardActions.find((action) => action.value === awardAction),
    [awardAction],
  );

  if (!isOpen) return null;

  return (
    <ModalBackground>
      <ModalContent>
        <Row data-width="full" data-justify="between">
          <ModalHeader>Award Student Coins</ModalHeader>
          <ModalClose onClick={onClose}>
            <IconX />
          </ModalClose>
        </Row>
        <Description>Reward the student in this tutoring session with coins.</Description>
        <Label>
          Award amount
          <Select
            value={awardAction}
            onChange={(event) => {
              setAwardAction(event.target.value);
            }}
          >
            <option value="">Select coin amount</option>
            {awardActions.map((action) => (
              <option key={action.value} value={action.value}>
                {action.key}
              </option>
            ))}
          </Select>
        </Label>
        <ButtonRow>
          <ButtonGeneric
            text={"Close"}
            textColor={"#4353FF"}
            border={"1px solid #CBD5E1"}
            padding={"0.875rem"}
            onClick={(unlockCallback) => {
              onClose();
              unlockCallback();
            }}
          />
          <ButtonGeneric
            text={"Send Award"}
            backgroundColor={"#4353FF"}
            padding={"0.875rem"}
            onClick={(unlockCallback) => {
              if (!studentUserId) {
                toast.error("Missing student user ID for this session.");
                unlockCallback();
                return;
              }

              if (!selectedAward) {
                toast.error("Please select a coin amount.");
                unlockCallback();
                return;
              }

              postBulkUpdateUserEvents({
                action: selectedAward.value,
                userIds: [studentUserId],
              })
                .then(async () => {
                  toast.success("Coins awarded!");
                  onClose();

                  try {
                    await postFlowTutorMessage(
                      flowGeneratorSessionId,
                      `Well done! Here's ${selectedAward.key} for your efforts!`,
                    );
                  } catch (error) {
                    toastAndWarn(
                      "Coins were awarded, but the follow-up message could not be sent.",
                      error,
                    );
                  }
                })
                .catch((error) => {
                  toastAndError("Awarding coins failed.", error);
                })
                .finally(() => {
                  unlockCallback();
                });
            }}
          />
        </ButtonRow>
      </ModalContent>
    </ModalBackground>
  );
};
